import { Injectable } from '@angular/core';

import { KeyboardEventCode } from '@recruitee/common/keyboard';

import { Placeholder, PlaceholderId } from '../../../wysiwyg-extensions/placeholders/placeholder';
import { PlaceholderService } from '../../../wysiwyg-extensions/placeholders/placeholder.service';
import { Redactor, RedactorApp, RedactorComponentType, RedactorConfig } from '../../redactor.types';
import { RedactorPlugin, Wrapper } from '../interfaces';
import { ANGULAR_ADAPTER_PLUGIN_TOKEN } from '../redactor-adapter';
import { mergeRedactorConfigArrField, RedactorExtension } from '../redactor-extension';

declare let $R: Redactor;

// plugin constants
export const PLACEHOLDER_PLUGIN_TOKEN = 'rt-placeholder';
export const PLACEHOLDER_ANGULAR_COMPONENT = 'rt-wysiwyg-placeholder';
export const PLACEHOLDER_TYPEAHEAD_ANGULAR_COMPONENT = 'rt-wysiwyg-placeholder-typeahead';

// redactor stuff

export class RtRedactorPlaceholder implements RedactorPlugin {
  private readonly placeholderRangeRegexp = /(=")?(\[[a-z|_.]{3,30}])/gm;
  private readonly placeholderTypeaheadRegexp = /(\[[a-z|_.]+[^\]|[])($)/;
  private readonly navigationButtons = [
    KeyboardEventCode.ArrowDown,
    KeyboardEventCode.ArrowUp,
    KeyboardEventCode.Enter,
  ];
  private dropdownNodeIds: string[] = [];
  private typeaheadMatch: string | null;
  private placeholdersMap: Record<string, Placeholder>;
  private placeholdersWithoutDeprecated: Placeholder[];

  private app: RedactorApp;
  private keycodes: any;
  private selection: any;
  private caret: any;
  private inspector: any;
  private utils: any;
  private insertion: any;
  private component: any;
  private opts: any;
  private cleaner: any;

  constructor(private placeholderService: PlaceholderService) {}

  public init(app: RedactorApp): void {
    this.app = app;
    this.cleaner = app.cleaner;
    this.opts = app.opts;
    this.component = app.component;
    this.utils = app.utils;
    this.insertion = app.insertion;
    this.inspector = app.inspector;
    this.caret = app.caret;
    this.selection = app.selection;
    this.keycodes = app.keycodes;
    this.placeholdersMap = {};
  }

  // eslint-disable-next-line max-lines-per-function
  public start(): void {
    this.placeholdersWithoutDeprecated =
      this.opts.rtPlaceholders?.filter(
        (placeholder: Placeholder) =>
          !this.placeholderService.deprecatedCollection.includes(<PlaceholderId>placeholder.id),
      ) || [];
    this.updatePlaceholdersMap();

    this.cleaner.addConvertRules(PLACEHOLDER_PLUGIN_TOKEN, ($wrapper: Wrapper) => {
      if (!this.opts.rtPlaceholders || this.opts.rtPlaceholders.length === 0) return;
      let $$wrapper = $wrapper;
      // console.time('placeholder_convert');

      const html = $$wrapper.html();
      const convertedHtml = html.replace(this.placeholderRangeRegexp, match => {
        if (match.startsWith('="')) return match;

        const placeholder = this.placeholdersMap[match];
        return placeholder ? this.createPlaceholderComponent(placeholder).get().outerHTML : match;
      });

      $$wrapper = $$wrapper.html(convertedHtml);

      if (this.opts.rtPlaceholdersExcludeParentSelector) {
        $$wrapper = $$wrapper
          .find(
            `${this.opts.rtPlaceholdersExcludeParentSelector} [data-angular-type="${PLACEHOLDER_ANGULAR_COMPONENT}"]`,
          )
          .replaceWith(node =>
            document.createTextNode(decodeURI(node.dataset['interpolatedValue'])),
          );
      }

      // console.timeEnd('placeholder_convert');
      return $$wrapper;
    });
    //
    this.cleaner.addUnconvertRules(PLACEHOLDER_PLUGIN_TOKEN, ($wrapper: Wrapper) => {
      // console.time('placeholder_unconvert');

      $wrapper
        .find(`[data-angular-type="${PLACEHOLDER_ANGULAR_COMPONENT}"]`)
        .replaceWith(node => document.createTextNode(decodeURI(node.dataset['interpolatedValue'])));

      // console.timeEnd('placeholder_unconvert');
      return $wrapper;
    });
  }

  public stop(): void {
    delete this.cleaner.convertRules[PLACEHOLDER_PLUGIN_TOKEN];
    delete this.cleaner.unconvertRules[PLACEHOLDER_PLUGIN_TOKEN];
  }

  public onkeyup(event: KeyboardEvent): void {
    this.checkTypeahead(event);
    this.replaceCompletePlaceholders();
  }

  public onkeydown(event: KeyboardEvent) {
    const navigationButtonsUsed = this.navigationButtons.includes(<KeyboardEventCode>event.code);
    this.dropdownNodeIds.forEach(id => {
      const dropdownNode = document.getElementById(id)?.parentElement;
      if (dropdownNode && this.isEventWithModifier(event)) {
        this.removeDropdown(dropdownNode);
      }
      if (dropdownNode && navigationButtonsUsed) {
        event.stopPropagation();
        event.preventDefault();
        const range = this.selection.getRange();
        range.collapse(true);
        range.setEndBefore(dropdownNode);
        this.selection.setRange(range);
      }
    });
  }

  private isEventWithModifier(event: KeyboardEvent) {
    return event.getModifierState('Meta') || event.getModifierState('Control');
  }

  private insertPlaceholder(placeholder: Placeholder): void {
    const $cmp = this.createPlaceholderComponent(placeholder);
    this.insertion.insertNode($cmp);
    return $cmp;
  }

  private insertPlaceholderFromDropdown(placeholder: Placeholder): void {
    this.selection.restore();
    this.changeTextToPlaceholder(this.selection.getCurrent(), this.typeaheadMatch!, placeholder);
  }

  private changeTextToPlaceholder(target: Node, textToReplace: string, placeholder: Placeholder) {
    const component = this.createPlaceholderComponent(placeholder);
    const node: HTMLElement = component.get();
    const nodeId = node.children[0].id;
    const newContent = target.textContent!.replace(textToReplace, node.outerHTML);
    $R.dom(target).replaceWith(newContent);
    const insertedPlaceholder = document.getElementById(nodeId)!;
    this.caret.setAfter(insertedPlaceholder.parentNode);
  }

  private replaceCompletePlaceholders() {
    const TEXT_NODE_TYPE = 3;
    const current: Node = this.selection.getCurrent();

    if (!current || current.nodeType !== TEXT_NODE_TYPE) return;

    const content = current.textContent!;
    const placeholderMatches = content.match(/(\[.*?\])/g);

    if (placeholderMatches?.length) {
      placeholderMatches.forEach(match => {
        const placeholder = this.placeholdersMap[match];
        if (!placeholder) return;
        this.changeTextToPlaceholder(current, match, placeholder);
      });
    }
  }

  private checkTypeahead(event: KeyboardEvent) {
    const navigationButtonsUsed = this.navigationButtons.includes(<KeyboardEventCode>event.code);
    this.dropdownNodeIds.forEach(id => {
      const dropdownNode = document.getElementById(id)?.parentElement;
      if (dropdownNode && !navigationButtonsUsed) {
        this.removeDropdown(dropdownNode);
      }
    });

    if (navigationButtonsUsed || event.code === KeyboardEventCode.Backspace) {
      return;
    }

    const placeholdersTypeaheadMatches = this.selection
      .getTextBeforeCaret(30)
      .match(this.placeholderTypeaheadRegexp);

    const placeholderSuggestions = placeholdersTypeaheadMatches
      ? [
          ...this.placeholdersWithoutDeprecated.filter((placeholder: Placeholder) =>
            placeholder.id.includes(placeholdersTypeaheadMatches[0].substring(1)),
          ),
        ]
      : [];

    if (!placeholderSuggestions.length) {
      this.typeaheadMatch = null;
      return;
    }
    this.typeaheadMatch = placeholdersTypeaheadMatches![0];
    this.insertSuggestionDropdown(placeholderSuggestions);
  }

  private insertSuggestionDropdown(placeholderSuggestions: Placeholder[]) {
    const component = this.createDropdownComponent(placeholderSuggestions);
    const node: HTMLElement = component.get();
    this.dropdownNodeIds = [...this.dropdownNodeIds, node.children[0].id];
    const range = this.selection.getRange();
    range.collapse(true);
    range.insertNode(node);
    range.setEndBefore(node);
    this.selection.setRange(range);
    this.selection.save();
  }

  private updatePlaceholdersData(placeholders: Placeholder[]): void {
    this.opts.rtPlaceholders = placeholders;

    this.placeholdersWithoutDeprecated = placeholders.filter(
      (placeholder: Placeholder) =>
        !this.placeholderService.deprecatedCollection.includes(<PlaceholderId>placeholder.id),
    );
    this.updatePlaceholdersMap();
  }

  private createPlaceholderComponent(placeholder: Placeholder): RedactorComponentType {
    const cmp$ = this.component.create(ANGULAR_ADAPTER_PLUGIN_TOKEN, {
      angularTagName: PLACEHOLDER_ANGULAR_COMPONENT,
      data: {
        active: placeholder,
        placeholders: this.opts.rtPlaceholders,
      },
    });

    cmp$.writeValue(placeholder.id);

    return cmp$;
  }

  private createDropdownComponent(placeholders: Placeholder[]): RedactorComponentType {
    return this.component.create(ANGULAR_ADAPTER_PLUGIN_TOKEN, {
      angularTagName: PLACEHOLDER_TYPEAHEAD_ANGULAR_COMPONENT,
      data: {
        placeholders: placeholders,
      },
    });
  }

  private removeDropdown(nodeElement: Node) {
    this.selection.save();
    const cmp = this.component.build(nodeElement);
    this.component.remove(cmp);
    this.selection.restore();
  }

  private updatePlaceholdersMap(): void {
    const placeholders: Placeholder[] = this.opts.rtPlaceholders || [];
    this.placeholdersMap = placeholders.reduce((acc, p) => {
      acc[p.toString()] = p;
      return acc;
    }, <Record<string, Placeholder>>{});
  }
}

@Injectable({
  providedIn: 'root',
})
export class RedactorPlaceholderExtension extends RedactorExtension {
  constructor(private placeholderService: PlaceholderService) {
    super();

    if (!$R.classes[PLACEHOLDER_PLUGIN_TOKEN]) {
      $R.add('plugin', PLACEHOLDER_PLUGIN_TOKEN, new RtRedactorPlaceholder(placeholderService));
    }
  }

  public canInstall(config: RedactorConfig): boolean {
    return (
      Array.isArray(config.plugins) &&
      Array.isArray(config.rtPlaceholders) &&
      config.plugins.includes(PLACEHOLDER_PLUGIN_TOKEN)
    );
  }

  public install(config: RedactorConfig): RedactorConfig {
    this.enable();

    return {
      ...config,
      plugins: mergeRedactorConfigArrField(config, 'plugins', [
        'variable',
        ANGULAR_ADAPTER_PLUGIN_TOKEN,
      ]),
    };
  }
}
